import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { fontLoader } from './utils/fontLoader'

/**
 * iOS環境を判定するユーティリティ関数
 */
const isIOSEnvironment = (): boolean => {
  return /iP(hone|od|ad)/i.test(navigator.userAgent)
}

/**
 * Service Worker登録を削除する
 */
const cleanupServiceWorkerRegistrations = async (): Promise<void> => {
  try {
    if (isIOSEnvironment()) {
      console.log('[SW] iOS環境でのService Worker処理を開始')
    }
    
    const registrations = await navigator.serviceWorker.getRegistrations()
    const unregisterPromises = registrations.map(registration => 
      registration.unregister().catch(err => {
        console.warn('[SW] Registration削除失敗:', err)
        return false
      })
    )
    
    await Promise.allSettled(unregisterPromises)
    console.log(`[SW] ${registrations.length}個のRegistrationを処理完了`)
  } catch (error) {
    console.warn('[SW] Registration削除処理でエラー:', error)
  }
}

/**
 * キャッシュストレージをクリーンアップする
 */
const cleanupCacheStorage = async (): Promise<void> => {
  try {
    if (!('caches' in window)) {
      console.log('[SW] Cache API未サポート')
      return
    }
    
    const cacheNames = await caches.keys()
    const deletePromises = cacheNames.map(cacheName => 
      caches.delete(cacheName).catch(err => {
        console.warn(`[SW] Cache削除失敗 ${cacheName}:`, err)
        return false
      })
    )
    
    await Promise.allSettled(deletePromises)
    console.log(`[SW] ${cacheNames.length}個のCacheを処理完了`)
  } catch (error) {
    console.warn('[SW] Cache削除処理でエラー:', error)
  }
}

/**
 * Service Worker関連のクリーンアップを実行
 * アプリ初期化に影響しないよう非同期で実行
 */
const performServiceWorkerCleanup = (): void => {
  if (!('serviceWorker' in navigator) || window.location.hostname === 'localhost') {
    return
  }

  Promise.allSettled([
    cleanupServiceWorkerRegistrations(),
    cleanupCacheStorage()
  ]).then(() => {
    console.log('[SW] クリーンアップ処理完了（エラーがあっても継続）')
  }).catch(error => {
    // Promise.allSettledは通常失敗しないが、念のため
    console.warn('[SW] 予期しないエラー:', error)
  })
}

/**
 * Reactアプリケーションを初期化・レンダリング
 */
const renderApp = (): void => {
  const rootElement = document.getElementById('root')
  if (!rootElement) {
    console.error('Root element not found')
    return
  }

  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

/**
 * アプリケーション初期化処理
 */
const initializeApp = (): void => {
  // フォント読み込み初期化
  fontLoader.initializeFonts()
  
  // React初期化を最優先で実行（SW処理とは独立）
  renderApp()
  
  // Service Worker後処理を非同期で実行
  performServiceWorkerCleanup()
}

// アプリケーション開始
initializeApp()

// To re-enable SW later, replace performServiceWorkerCleanup() with:
// window.addEventListener('load', () => {
//   navigator.serviceWorker.register('/sw.js')
//     .then(reg => console.log('SW registered:', reg))
//     .catch(err => console.log('SW registration failed:', err));
// });